import { get, round } from 'lodash'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  InputProps,
  MenuItem,
  Typography,
} from '@material-ui/core'
import React, { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'react-feather'
import {
  Control,
  DeepMap,
  FieldError,
  FieldErrors,
  RegisterOptions,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { i18n } from '../../../../i18n'
import {
  areaUnit,
  temperatureUnit,
  uaValueUnit,
  uValueUnit,
} from '../../../../util/formatters'
import { validators } from '../../../../validation/validators'
import { ControlledTextField } from '../../../form/ControlledTextField'
import { FormGroup } from '../../../form/FormGroup'
import { FormValues } from '../form-values'
import { theme } from '../../../../theme'
import { ScheduleFragment } from '../../../../generated/graphql'
import { parseNumber } from './energy-system-params-util'

const centeredTextField: InputProps = {
  margin: 'dense',
  inputProps: { style: { textAlign: 'center' } },
}

type Props = {
  schedules: ScheduleFragment[]
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
}

type CustomUvalueFieldProps = {
  control: Control
  errors: DeepMap<FormValues, FieldError>
  name: string
  valueUpdateMethod: (value: React.SetStateAction<number | undefined>) => void
  rules?: RegisterOptions
  label?: string
}

function CustomUvalueField({
  control,
  errors,
  name,
  valueUpdateMethod,
  rules = {
    ...validators.required,
    ...validators.decimal,
  },
  label,
}: CustomUvalueFieldProps): React.ReactElement {
  return (
    <ControlledTextField
      control={control}
      name={name}
      error={get(errors, name)} // eslint-disable-line
      label={label}
      rules={rules}
      onBlur={async (event) =>
        valueUpdateMethod(await parseNumber(control, event))
      }
      InputProps={centeredTextField}
    />
  )
}

type CustomUvalueRowProps = {
  control: Control
  errors: DeepMap<FormValues, FieldError>
  rowLabel: string
  areaFieldName: string
  areaUpdateMethod: (value: React.SetStateAction<number | undefined>) => void
  uValueFieldName: string
  uValueUpdateMethod: (value: React.SetStateAction<number | undefined>) => void
  uAvalue: number | undefined
}

function CustomUvalueRow({
  control,
  errors,
  rowLabel,
  areaFieldName,
  areaUpdateMethod,
  uValueFieldName,
  uValueUpdateMethod,
  uAvalue,
}: CustomUvalueRowProps): React.ReactElement {
  return (
    <Grid
      container
      direction="row"
      item
      alignItems="center"
      spacing={1}
      style={{ marginBottom: '1px' }}
    >
      <Grid item xs={3}>
        <Typography>{i18n.t(rowLabel)}</Typography>
      </Grid>
      <Grid item xs={3}>
        <CustomUvalueField
          control={control}
          errors={errors}
          name={areaFieldName}
          valueUpdateMethod={areaUpdateMethod}
        />
      </Grid>
      <Grid item xs={3}>
        <CustomUvalueField
          control={control}
          errors={errors}
          name={uValueFieldName}
          valueUpdateMethod={uValueUpdateMethod}
        />
      </Grid>
      <Grid item xs={3}>
        <Typography align="center">{uAvalue ?? ''}</Typography>
      </Grid>
    </Grid>
  )
}

type CustomCalibrationRowProps = {
  control: Control
  errors: DeepMap<FormValues, FieldError>
  rowLabel: string
  fieldName: string
  fieldUnit: string
}

function CustomCalibrationRow({
  control,
  errors,
  rowLabel,
  fieldName,
  fieldUnit,
}: CustomCalibrationRowProps): React.ReactElement {
  return (
    <Grid container direction="row" item alignItems="center">
      <Grid item xs={9}>
        <Typography>{i18n.t(rowLabel)}</Typography>
      </Grid>
      <Grid item xs={3}>
        <ControlledTextField
          control={control}
          name={fieldName}
          error={get(errors, fieldName)} // eslint-disable-line
          rules={{
            ...validators.required,
            ...validators.decimal,
          }}
          InputProps={{
            margin: 'dense',
            endAdornment: <Typography>{fieldUnit}</Typography>,
          }}
        />
      </Grid>
    </Grid>
  )
}

export function SpaceHeatingForm({
  schedules,
  control,
  errors,
}: Props): React.ReactElement {
  const wallUAvalueRef = useRef<number | undefined>(0)
  const roofUAvalueRef = useRef<number | undefined>(0)
  const baseFloorUAvalueRef = useRef<number | undefined>(0)
  const windowUAvalueRef = useRef<number | undefined>(0)
  const doorUAvalueRef = useRef<number | undefined>(0)

  const totalAreaRef = useRef<number | undefined>(0)
  const totalUAvalueRef = useRef<number | undefined>(0)
  const heatBridgeUAvalueRef = useRef<number | undefined>(0)
  const airLeakegeUAvalueRef = useRef<number | undefined>(0)

  const heavyHeatLossesUAvalueRef = useRef<number | undefined>(0)
  const lightHeatLossesUAvalueRef = useRef<number | undefined>(0)
  const totalHeatLossesUAvalueRef = useRef<number | undefined>(0)

  const [wallArea, setWallArea] = useState<number | undefined>(0)
  const [roofArea, setRoofArea] = useState<number | undefined>(0)
  const [baseFloorArea, setBaseFloorArea] = useState<number | undefined>(0)
  const [windowArea, setWindowArea] = useState<number | undefined>(0)
  const [doorArea, setDoorArea] = useState<number | undefined>(0)
  const [wallUvalue, setWallUvalue] = useState<number | undefined>(0)
  const [roofUvalue, setRoofUvalue] = useState<number | undefined>(0)
  const [baseUvalue, setBaseUvalue] = useState<number | undefined>(0)
  const [windowUvalue, setWindowUvalue] = useState<number | undefined>(0)
  const [doorUvalue, setDoorUvalue] = useState<number | undefined>(0)

  const [hbRatio, setHbRatio] = useState<number | undefined>(0)
  const [airLeakageq50, setAirLeakageq50] = useState<number | undefined>(0)
  const [airLeakageFactor, setAirLeakageFactor] = useState<number | undefined>(
    0,
  )

  const [heatSupplyEff, setHeatSupplyEff] = useState<number | undefined>(0)
  const [basEff, setBasEff] = useState<number | undefined>(0)

  const { t } = useTranslation()

  const calcUAvalue = (
    area: number | undefined,
    uValue: number | undefined,
    uAvalueRef: React.MutableRefObject<number | undefined>,
  ) => {
    uAvalueRef.current = undefined // eslint-disable-line

    if (area !== undefined && uValue !== undefined)
      uAvalueRef.current = round(area * uValue, 1) // eslint-disable-line

    return uAvalueRef.current // eslint-disable-line
  }

  const calcSums = (
    sumRef: React.MutableRefObject<number | undefined>,
    precision: number,
    ...nums: (number | undefined)[]
  ) => {
    sumRef.current = 0 // eslint-disable-line

    for (let i = 0; i < nums.length; i++) {
      const num = nums[i]

      if (num === undefined) {
        sumRef.current = undefined // eslint-disable-line
        return undefined
      }

      sumRef.current += num // eslint-disable-line
    }

    return round(sumRef.current, precision)
  }

  const calcHeatBridgeUAvalue = () => {
    heatBridgeUAvalueRef.current = undefined

    if (hbRatio !== undefined && totalUAvalueRef.current !== undefined)
      heatBridgeUAvalueRef.current = round(
        (hbRatio / 100) * totalUAvalueRef.current,
        1,
      )

    return heatBridgeUAvalueRef.current
  }

  const calcAirLeakageUAvalue = () => {
    airLeakegeUAvalueRef.current = undefined

    if (
      totalAreaRef.current !== undefined &&
      airLeakageq50 !== undefined &&
      airLeakageFactor !== undefined &&
      airLeakageFactor !== 0
    )
      airLeakegeUAvalueRef.current = round(
        (((airLeakageq50 / airLeakageFactor) * totalAreaRef.current) / 3.6) *
          1.2,
        1,
      )

    return airLeakegeUAvalueRef.current
  }

  const calcTotalSpaceHeating = () => {
    let result: number | undefined

    if (
      totalHeatLossesUAvalueRef.current !== undefined &&
      heatSupplyEff !== undefined &&
      basEff !== undefined
    )
      result = round(
        totalHeatLossesUAvalueRef.current / heatSupplyEff / basEff,
        1,
      )

    return result
  }

  useEffect(() => {
    setWallArea(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating
          ?.exteriorWallArea,
      ),
    )

    setRoofArea(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating?.roofArea,
      ),
    )

    setBaseFloorArea(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating
          ?.baseFloorArea,
      ),
    )

    setWindowArea(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating?.windowsArea,
      ),
    )

    setDoorArea(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating?.doorsArea,
      ),
    )

    setWallUvalue(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating
          ?.exteriorWallUvalue,
      ),
    )

    setRoofUvalue(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating?.roofUvalue,
      ),
    )

    setBaseUvalue(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating
          ?.baseFloorUvalue,
      ),
    )

    setWindowUvalue(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating
          ?.windowsUvalue,
      ),
    )

    setDoorUvalue(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating?.doorsUvalue,
      ),
    )

    setHbRatio(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating
          ?.heatBridgeRatio,
      ),
    )

    setAirLeakageq50(
      Number(control.defaultValuesRef.current.parameters?.spaceHeating?.q50),
    )

    setAirLeakageFactor(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating
          ?.airLeakageXfactor,
      ),
    )

    setHeatSupplyEff(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating
          ?.heatSupplyEfficiency,
      ),
    )

    setBasEff(
      Number(
        control.defaultValuesRef.current.parameters?.spaceHeating
          ?.basEfficiency,
      ),
    )
  }, []) // eslint-disable-line

  return (
    <FormGroup width="100%">
      <Accordion elevation={3}>
        <AccordionSummary
          expandIcon={<ChevronDown id="collection-cooling-expand-button" />}
        >
          <Box display="flex" alignItems="center">
            <Typography>
              {t('energySystem.parameters.spaceHeating.tabs.heatLosses')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="column" spacing={0}>
            <Grid
              container
              direction="row"
              item
              alignItems="flex-end"
              style={{ marginBottom: '5px' }}
            >
              <Grid item xs={3}>
                <Typography variant="body2">
                  {t(
                    'energySystem.parameters.spaceHeating.exteriorEnvelopeLabel',
                  )}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" align="center">
                  {t('unitName.area')} [{areaUnit}]
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" align="center">
                  {t('energySystem.parameters.spaceHeating.UvalueLabel')} [
                  {uValueUnit}]
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="body2" align="center">
                  {t('energySystem.parameters.spaceHeating.UAvalueLabel')} [
                  {uaValueUnit}]
                </Typography>
              </Grid>
            </Grid>

            <CustomUvalueRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.exteriorWallLabel"
              areaFieldName="parameters.spaceHeating.exteriorWallArea"
              areaUpdateMethod={setWallArea}
              uValueFieldName="parameters.spaceHeating.exteriorWallUvalue"
              uValueUpdateMethod={setWallUvalue}
              uAvalue={calcUAvalue(wallArea, wallUvalue, wallUAvalueRef)}
            />
            <CustomUvalueRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.roofLabel"
              areaFieldName="parameters.spaceHeating.roofArea"
              areaUpdateMethod={setRoofArea}
              uValueFieldName="parameters.spaceHeating.roofUvalue"
              uValueUpdateMethod={setRoofUvalue}
              uAvalue={calcUAvalue(roofArea, roofUvalue, roofUAvalueRef)}
            />
            <CustomUvalueRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.baseFloorLabel"
              areaFieldName="parameters.spaceHeating.baseFloorArea"
              areaUpdateMethod={setBaseFloorArea}
              uValueFieldName="parameters.spaceHeating.baseFloorUvalue"
              uValueUpdateMethod={setBaseUvalue}
              uAvalue={calcUAvalue(
                baseFloorArea,
                baseUvalue,
                baseFloorUAvalueRef,
              )}
            />
            <CustomUvalueRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.windowsLabel"
              areaFieldName="parameters.spaceHeating.windowsArea"
              areaUpdateMethod={setWindowArea}
              uValueFieldName="parameters.spaceHeating.windowsUvalue"
              uValueUpdateMethod={setWindowUvalue}
              uAvalue={calcUAvalue(windowArea, windowUvalue, windowUAvalueRef)}
            />
            <CustomUvalueRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.doorsLabel"
              areaFieldName="parameters.spaceHeating.doorsArea"
              areaUpdateMethod={setDoorArea}
              uValueFieldName="parameters.spaceHeating.doorsUvalue"
              uValueUpdateMethod={setDoorUvalue}
              uAvalue={calcUAvalue(doorArea, doorUvalue, doorUAvalueRef)}
            />
            <Box my={1}>
              <Divider />
            </Box>
            <Grid
              container
              direction="row"
              spacing={1}
              item
              alignItems="flex-end"
            >
              <Grid item xs={3}>
                <Typography>{t('common.total')}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography align="center">
                  {calcSums(
                    totalAreaRef,
                    0,
                    wallArea,
                    roofArea,
                    baseFloorArea,
                    windowArea,
                    doorArea,
                  ) ?? ''}
                </Typography>
              </Grid>
              <Grid item xs={3} />
              <Grid item xs={3}>
                <Typography align="center">
                  {calcSums(
                    totalUAvalueRef,
                    1,
                    wallUAvalueRef.current,
                    roofUAvalueRef.current,
                    baseFloorUAvalueRef.current,
                    windowUAvalueRef.current,
                    doorUAvalueRef.current,
                  ) ?? ''}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={1}
              item
              alignItems="center"
              style={{ marginTop: theme.spacing(3), marginBottom: '1px' }}
            >
              <Grid item xs={3}>
                <Typography>
                  {t('energySystem.parameters.spaceHeating.heatBridgeRatio')}{' '}
                  [%]
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <CustomUvalueField
                  control={control}
                  errors={errors}
                  name="parameters.spaceHeating.heatBridgeRatio"
                  valueUpdateMethod={setHbRatio}
                />
              </Grid>
              <Grid item xs={3} />
              <Grid item xs={3}>
                <Typography align="center">
                  {calcHeatBridgeUAvalue() ?? ''}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={1}
              item
              alignItems="center"
              style={{ marginBottom: '1px' }}
            >
              <Grid item xs={3}>
                <Typography>
                  {t('energySystem.parameters.spaceHeating.airLeakage')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <CustomUvalueField
                  control={control}
                  errors={errors}
                  label={t(
                    'energySystem.parameters.spaceHeating.airLeakageq50',
                  )}
                  name="parameters.spaceHeating.q50"
                  valueUpdateMethod={setAirLeakageq50}
                />
              </Grid>
              <Grid item xs={3}>
                <CustomUvalueField
                  control={control}
                  errors={errors}
                  label={t(
                    'energySystem.parameters.spaceHeating.airLeakageXfactor',
                  )}
                  name="parameters.spaceHeating.airLeakageXfactor"
                  valueUpdateMethod={setAirLeakageFactor}
                  rules={{
                    ...validators.required,
                    ...validators.positiveDecimal,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <Typography align="center">
                  {calcAirLeakageUAvalue() ?? ''}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={1}
              item
              alignItems="center"
              style={{ marginTop: theme.spacing(2) }}
            >
              <Grid item xs={9}>
                <Typography>
                  {t(
                    'energySystem.parameters.spaceHeating.heavyHeatLossesLabel',
                  )}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography align="center">
                  {calcSums(
                    heavyHeatLossesUAvalueRef,
                    1,
                    wallUAvalueRef.current,
                    roofUAvalueRef.current,
                    baseFloorUAvalueRef.current,
                  ) ?? ''}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={1}
              item
              alignItems="center"
            >
              <Grid item xs={9}>
                <Typography>
                  {t(
                    'energySystem.parameters.spaceHeating.lightHeatLossesLabel',
                  )}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography align="center">
                  {calcSums(
                    lightHeatLossesUAvalueRef,
                    1,
                    windowUAvalueRef.current,
                    doorUAvalueRef.current,
                    heatBridgeUAvalueRef.current,
                    airLeakegeUAvalueRef.current,
                  ) ?? ''}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={1}
              item
              alignItems="center"
            >
              <Grid item xs={9}>
                <Typography>
                  {t(
                    'energySystem.parameters.spaceHeating.totalHeatLossesLabel',
                  )}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography align="center">
                  {calcSums(
                    totalHeatLossesUAvalueRef,
                    1,
                    heavyHeatLossesUAvalueRef.current,
                    lightHeatLossesUAvalueRef.current,
                  ) ?? ''}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              spacing={1}
              item
              alignItems="center"
              style={{ marginTop: theme.spacing(3), marginBottom: '1px' }}
            >
              <Grid item xs={6}>
                <Typography>
                  {t(
                    'energySystem.parameters.spaceHeating.heatSupplyEfficiency',
                  )}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <CustomUvalueField
                  control={control}
                  errors={errors}
                  name="parameters.spaceHeating.heatSupplyEfficiency"
                  valueUpdateMethod={setHeatSupplyEff}
                  rules={{
                    ...validators.required,
                    ...validators.efficiency,
                  }}
                />
              </Grid>
              <Grid item xs={3} />
            </Grid>
            <Grid
              container
              direction="row"
              spacing={1}
              item
              alignItems="center"
            >
              <Grid item xs={6}>
                <Typography>
                  {t('energySystem.parameters.spaceHeating.basEfficiency')}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <CustomUvalueField
                  control={control}
                  errors={errors}
                  name="parameters.spaceHeating.basEfficiency"
                  valueUpdateMethod={setBasEff}
                  rules={{
                    ...validators.required,
                    ...validators.efficiency,
                  }}
                />
              </Grid>
              <Grid item xs={3} />
            </Grid>
            <Grid
              container
              direction="row"
              spacing={1}
              item
              alignItems="center"
              style={{ marginTop: theme.spacing(2) }}
            >
              <Grid item xs={9}>
                <Typography>
                  {t(
                    'energySystem.parameters.spaceHeating.totalSpaceHeatingLabel',
                  )}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography align="center">
                  {calcTotalSpaceHeating() ?? ''}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              item
              alignItems="center"
              style={{ marginTop: theme.spacing(2) }}
            >
              <Grid item xs={6}>
                <Typography>
                  {t('energySystem.parameters.spaceHeating.usageProfile')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  control={control}
                  name="scheduleId"
                  error={errors.scheduleId}
                  rules={validators.required}
                  select
                  InputProps={{
                    margin: 'dense',
                  }}
                >
                  {Object.values(schedules).map((schedule) => (
                    <MenuItem key={schedule.id} value={schedule.id}>
                      {schedule.name}
                    </MenuItem>
                  ))}
                </ControlledTextField>
              </Grid>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion style={{ marginTop: theme.spacing(1) }} elevation={3}>
        <AccordionSummary
          expandIcon={<ChevronDown id="collection-cooling-expand-button" />}
        >
          <Box display="flex" alignItems="center">
            <Typography>
              {t(
                'energySystem.parameters.spaceHeating.tabs.calculationParameters',
              )}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container direction="column" spacing={1}>
            <CustomCalibrationRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.affectingHeatLossStartTemp"
              fieldName="parameters.spaceHeating.affectingHeatLossStartTemp"
              fieldUnit={temperatureUnit}
            />
            <CustomCalibrationRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.dynamicOutTempWeightFactor"
              fieldName="parameters.spaceHeating.dynamicOutTempWeightFactor"
              fieldUnit="%"
            />
            <CustomCalibrationRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.heatLossesCorrectionFactor"
              fieldName="parameters.spaceHeating.heatLossesCorrectionFactor"
              fieldUnit="%"
            />
            <CustomCalibrationRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.heatLossesCorrectionFactorStartTemp"
              fieldName="parameters.spaceHeating.heatLossesCorrectionFactorStartTemp"
              fieldUnit={temperatureUnit}
            />
            <CustomCalibrationRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.supplyAirHeatingStartTemp"
              fieldName="parameters.spaceHeating.supplyAirHeatingStartTemp"
              fieldUnit={temperatureUnit}
            />
            <CustomCalibrationRow
              control={control}
              errors={errors}
              rowLabel="energySystem.parameters.spaceHeating.supplyAirFullHeatingStartTemp"
              fieldName="parameters.spaceHeating.supplyAirFullHeatingStartTemp"
              fieldUnit={temperatureUnit}
            />
          </Grid>
        </AccordionDetails>
      </Accordion>
    </FormGroup>
  )
}
